<script setup lang="ts">
import { computed } from 'vue'
import { BellAlertIcon } from '@heroicons/vue/24/outline'
import type { IAreaAlarmStat, IAreaClassificationStat } from '@/interfaces/entities/management'
import { translateAlarmType } from '@/interfaces/enums'

const props = defineProps<{
  alarms: IAreaAlarmStat[]
  classifications: IAreaClassificationStat[]
}>()

const total = computed(() => props.alarms.reduce((sum, alarm) => sum + alarm.count, 0))
const totalOpen = computed(() => props.alarms.reduce((sum, alarm) => sum + alarm.open_count, 0))
const totalStopped = computed(() => props.classifications.reduce((sum, c) => sum + c.stopped_minutes, 0))

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins ? `${hours}h ${mins}min` : `${hours}h`
}
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center gap-2">
      <BellAlertIcon class="h-5 w-5 text-slate-500" />
      <h3 class="text-base font-bold text-slate-800">Alarmes e paradas no período</h3>
    </div>

    <!-- KPIs -->
    <div class="grid grid-cols-3 gap-3">
      <div class="border border-stroke bg-white px-4 py-3">
        <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Total de alarmes</p>
        <p class="mt-0.5 text-3xl font-extrabold text-slate-900">{{ total }}</p>
      </div>
      <div class="border px-4 py-3" :class="totalOpen ? 'border-red-300 bg-red-50' : 'border-stroke bg-white'">
        <p class="text-[11px] font-medium uppercase tracking-wide" :class="totalOpen ? 'text-red' : 'text-slate-500'">Em aberto</p>
        <p class="mt-0.5 text-3xl font-extrabold" :class="totalOpen ? 'text-red' : 'text-slate-900'">{{ totalOpen }}</p>
      </div>
      <div class="border border-amber-300 bg-amber-50 px-4 py-3">
        <p class="text-[11px] font-medium uppercase tracking-wide text-amber-700">Tempo total de parada</p>
        <p class="mt-0.5 whitespace-nowrap text-3xl font-extrabold text-amber-700">{{ formatDuration(totalStopped) }}</p>
      </div>
    </div>

    <!-- Por tipo de alarme -->
    <div>
      <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Por tipo de alarme</h4>
      <div v-if="alarms.length" class="overflow-x-auto border border-stroke bg-white">
        <table class="w-full text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th class="px-4 py-2.5">Tipo</th>
              <th class="px-4 py-2.5 text-right">Quantidade</th>
              <th class="px-4 py-2.5 text-right">Em aberto</th>
              <th class="px-4 py-2.5 text-right">Tempo médio</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="alarm in alarms" :key="alarm.alarm_type" class="border-t border-stroke">
              <td class="px-4 py-2.5 font-medium text-slate-700">{{ translateAlarmType(alarm.alarm_type) }}</td>
              <td class="px-4 py-2.5 text-right text-base font-bold text-slate-900">{{ alarm.count }}</td>
              <td class="px-4 py-2.5 text-right text-base font-semibold" :class="alarm.open_count ? 'text-red' : 'text-slate-400'">
                {{ alarm.open_count }}
              </td>
              <td class="px-4 py-2.5 text-right text-slate-700">{{ formatDuration(alarm.avg_duration_minutes) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-sm text-slate-500">Nenhum alarme gerado neste período.</p>
    </div>

    <!-- Paradas por classificação -->
    <div v-if="classifications.length">
      <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Paradas por classificação</h4>
      <div class="overflow-x-auto border border-stroke bg-white">
        <table class="w-full text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th class="px-4 py-2.5">Classificação</th>
              <th class="px-4 py-2.5 text-right">Ocorrências</th>
              <th class="px-4 py-2.5 text-right">Tempo de parada</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in classifications" :key="item.classification" class="border-t border-stroke">
              <td class="px-4 py-2.5 font-medium text-slate-700">{{ item.classification }}</td>
              <td class="px-4 py-2.5 text-right text-base font-bold text-slate-900">{{ item.count }}</td>
              <td class="px-4 py-2.5 text-right text-base font-bold text-amber-700">{{ formatDuration(item.stopped_minutes) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
